import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { HandHeart, Package, Search, Store, Users, Wrench } from 'lucide-react';
import { getApiBaseUrl } from '../../config/apiBase';
import '../../styles/components/NeedsPage.css';

const responseTypes = [
  { value: 'Message', label: 'Send a message' },
  { value: 'OfferItem', label: 'Give one of my items' },
  { value: 'OfferService', label: 'Offer one of my services' },
  { value: 'SuggestMarketplaceItem', label: 'Suggest a marketplace item' },
  { value: 'SuggestGroup', label: 'Suggest a group' },
];

const categories = ['General', 'Food', 'Housing', 'Transportation', 'Prayer', 'Care', 'Tools', 'Financial', 'Volunteers'];

export default function NeedsPage() {
  const { getAccessTokenSilently, user } = useAuth0();
  const apiUrl = getApiBaseUrl();
  const initialParams = useMemo(() => new URLSearchParams(window.location.search || ''), []);
  const initialGroupId = initialParams.get('groupId') || '';
  const shouldOpenPostForm = initialParams.get('post') === '1';
  const [needs, setNeeds] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingResponses, setViewingResponses] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responseError, setResponseError] = useState('');
  const showResponses = async (need) => {
    setViewingResponses(need); setResponses([]); setResponseError('Loading responses...');
    try {
      const token = await getAccessTokenSilently();
      const result = await fetch(`${apiUrl}/api/needs/${need.id}/responses`, { headers: { Authorization: `Bearer ${token}` } });
      if (!result.ok) throw new Error();
      setResponses(await result.json()); setResponseError('');
    } catch { setResponseError('Could not load responses. Please retry.'); }
  };
  const [activeNeed, setActiveNeed] = useState(null);
  const [responseForm, setResponseForm] = useState({
    responseKind: initialParams.get('offerAssetId') ? 'OfferItem' : 'Message',
    message: '',
    offeredAssetId: initialParams.get('offerAssetId') || '',
    offeredServiceId: '',
    suggestedMarketplaceAssetId: '',
    suggestedGroupId: '',
  });
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: 'General',
    city: '',
    state: '',
    country: 'United States',
    groupId: initialGroupId,
  });
  const [myAssets, setMyAssets] = useState([]);
  const [services, setServices] = useState([]);
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [postingNeed, setPostingNeed] = useState(false);
  const [notice, setNotice] = useState('');

  const loadNeeds = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } });
      const params = new URLSearchParams();
      if (query.trim()) params.set('search', query.trim());
      if (initialGroupId) params.set('groupId', initialGroupId);
      const search = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${apiUrl}/api/needs${search}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`Needs request failed (${response.status})`);
      const data = await response.json();
      setNeeds(Array.isArray(data) ? data : []);
    } catch (loadError) {
      console.error('Error loading needs:', loadError);
      setError('Could not load needs right now.');
      setNeeds([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getAccessTokenSilently, initialGroupId, query]);

  const loadReferenceData = useCallback(async () => {
    if (!user?.sub) return;

    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } });
      const headers = { Authorization: `Bearer ${token}` };
      const [assetsResponse, servicesResponse, marketplaceResponse, groupsResponse] = await Promise.all([
        fetch(`${apiUrl}/api/assets/user/${encodeURIComponent(user.sub)}`, { headers }),
        fetch(`${apiUrl}/api/services`, { headers }),
        fetch(`${apiUrl}/api/assets`, { headers }),
        fetch(`${apiUrl}/api/GroupsTbls`, { headers }),
      ]);

      const loadedGroups = groupsResponse.ok ? await groupsResponse.json() : [];
      const assets = assetsResponse.ok ? await assetsResponse.json() : [];
      setMyAssets(assets.filter((asset) => asset.availability && !asset.isDeleted && !asset.ownedByGroupId && (!asset.checkedOut || (asset.checkedIn && new Date(asset.checkedIn) >= new Date(asset.checkedOut)))));
      setServices(servicesResponse.ok ? await servicesResponse.json() : []);
      setMarketplaceItems(marketplaceResponse.ok ? await marketplaceResponse.json() : []);
      setGroups(loadedGroups);
    } catch (referenceError) {
      console.error('Error loading need response options:', referenceError);
    }
  }, [apiUrl, getAccessTokenSilently, initialGroupId, user?.sub]);

  useEffect(() => {
    document.title = 'Needs | ThousandYearKingdom.org';
  }, []);

  useEffect(() => {
    loadNeeds();
  }, [loadNeeds]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  const localNeeds = useMemo(() => needs.filter((need) => need.isLocal), [needs]);

  const submitNeed = async (event) => {
    event.preventDefault();
    setPostingNeed(true);
    setNotice('');

    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } });
      const payload = {
        ...createForm,
        groupId: createForm.groupId ? Number(createForm.groupId) : null,
      };
      const response = await fetch(`${apiUrl}/api/needs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Create need failed (${response.status})`);
      setCreateForm({ title: '', description: '', category: 'General', city: '', state: '', country: 'United States', groupId: '' });
      setNotice('Need posted. The Body can now see it and respond.');
      await loadNeeds();
    } catch (submitError) {
      console.error('Error posting need:', submitError);
      setNotice('Could not post that need yet. Add a title and a clear description, then retry.');
    } finally {
      setPostingNeed(false);
    }
  };

  const submitResponse = async (event) => {
    event.preventDefault();
    if (!activeNeed) return;

    setSubmitting(true);
    setNotice('');

    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } });
      const payload = {
        responseKind: responseForm.responseKind,
        message: responseForm.message,
        offeredAssetId: responseForm.responseKind === 'OfferItem' && responseForm.offeredAssetId ? Number(responseForm.offeredAssetId) : null,
        offeredServiceId: responseForm.responseKind === 'OfferService' && responseForm.offeredServiceId ? Number(responseForm.offeredServiceId) : null,
        suggestedMarketplaceAssetId: responseForm.responseKind === 'SuggestMarketplaceItem' && responseForm.suggestedMarketplaceAssetId ? Number(responseForm.suggestedMarketplaceAssetId) : null,
        suggestedGroupId: responseForm.responseKind === 'SuggestGroup' && responseForm.suggestedGroupId ? Number(responseForm.suggestedGroupId) : null,
      };
      const response = await fetch(`${apiUrl}/api/needs/${activeNeed.id}/responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Need response failed (${response.status})`);
      setResponseForm({
        responseKind: 'Message',
        message: '',
        offeredAssetId: '',
        offeredServiceId: '',
        suggestedMarketplaceAssetId: '',
        suggestedGroupId: '',
      });
      setActiveNeed(null);
      setNotice('Response sent. Thank you for answering a real need.');
      await loadNeeds();
    } catch (submitError) {
      console.error('Error responding to need:', submitError);
      setNotice('Could not send that response yet. Please add a short message and retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectInputForResponse = () => {
    if (responseForm.responseKind === 'OfferItem') {
      return (
        <SelectField label="Inventory item" value={responseForm.offeredAssetId} onChange={(value) => setResponseForm((current) => ({ ...current, offeredAssetId: value }))}>
          <option value="">Choose one of your items</option>
          {myAssets.map((asset) => <option key={asset.id} value={asset.id}>{asset.assetName || asset.productName}</option>)}
        </SelectField>
      );
    }

    if (responseForm.responseKind === 'OfferService') {
      return (
        <SelectField label="Service" value={responseForm.offeredServiceId} onChange={(value) => setResponseForm((current) => ({ ...current, offeredServiceId: value }))}>
          <option value="">Choose a service</option>
          {services.map((service) => <option key={service.id} value={service.id}>{service.title}</option>)}
        </SelectField>
      );
    }

    if (responseForm.responseKind === 'SuggestMarketplaceItem') {
      return (
        <SelectField label="Marketplace item" value={responseForm.suggestedMarketplaceAssetId} onChange={(value) => setResponseForm((current) => ({ ...current, suggestedMarketplaceAssetId: value }))}>
          <option value="">Choose a marketplace item</option>
          {marketplaceItems.map((asset) => <option key={asset.id} value={asset.id}>{asset.productName || asset.assetName}</option>)}
        </SelectField>
      );
    }

    if (responseForm.responseKind === 'SuggestGroup') {
      return (
        <SelectField label="Group" value={responseForm.suggestedGroupId} onChange={(value) => setResponseForm((current) => ({ ...current, suggestedGroupId: value }))}>
          <option value="">Choose a group</option>
          {groups.map((group) => <option key={group.id} value={group.id}>{group.groupName}</option>)}
        </SelectField>
      );
    }

    return null;
  };

  return (
    <main className="needs-page">
      <section className="needs-hero">
        <div>
          <span className="needs-kicker"><HandHeart className="h-4 w-4" /> Kingdom Movement</span>
          <h1>Find needs. Answer with love.</h1>
          <p>
            TYK is not a Christian social feed. It is a place where the Body of Christ can see real needs, offer what God has placed in our hands, and move together so earth better reflects the Kingdom of Heaven.
          </p>
        </div>
        <form className="needs-search" onSubmit={(event) => { event.preventDefault(); loadNeeds(); }}>
          <label>
            <span>Search needs</span>
            <div>
              <Search className="h-4 w-4" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="food, transportation, prayer, shelter..." />
            </div>
          </label>
          <button type="submit">Search</button>
        </form>
      </section>

      {initialParams.get('offerAssetId') && <p className="needs-notice">Choose a need below to offer your inventory item as a gift. Sending an offer does not transfer the item; arrange the handoff with the recipient.</p>}
      {notice && <p className="needs-notice">{notice}</p>}

      <section className="needs-layout">
        <form className={`needs-post-card ${shouldOpenPostForm ? 'needs-post-card-highlight' : ''}`} onSubmit={submitNeed}>
          <span className="needs-kicker">Post a need</span>
          <h2>Let the Body know what is needed.</h2>
          <input value={createForm.title} onChange={(event) => setCreateForm((current) => ({ ...current, title: event.target.value }))} placeholder="Short title" />
          <textarea value={createForm.description} onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))} placeholder="What happened? What kind of help would actually bless this person, family, church, or group?" rows={5} />
          <select value={createForm.groupId} onChange={(event) => setCreateForm((current) => ({ ...current, groupId: event.target.value }))} aria-label="Post as">
            <option value="">Post as myself</option>
            {groups.map((group) => <option key={group.id} value={group.id}>Post for {group.groupName}</option>)}
          </select>
          <select value={createForm.category} onChange={(event) => setCreateForm((current) => ({ ...current, category: event.target.value }))}>
            {categories.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
          <div className="needs-location-grid">
            <input value={createForm.city} onChange={(event) => setCreateForm((current) => ({ ...current, city: event.target.value }))} placeholder="City" />
            <input value={createForm.state} onChange={(event) => setCreateForm((current) => ({ ...current, state: event.target.value }))} placeholder="State" />
          </div>
          <button type="submit" disabled={postingNeed}>{postingNeed ? 'Posting...' : 'Post need'}</button>
        </form>

        <section className="needs-results" aria-live="polite">
          <div className="needs-results-heading">
            <div>
              <span className="needs-kicker">Open needs</span>
              <h2>{localNeeds.length ? 'Local needs first' : 'Needs across the Body'}</h2>
            </div>
            <Link to="/marketplace">Offer goods through the marketplace</Link>
          </div>

          {loading && <p className="needs-empty">Loading needs...</p>}
          {error && <p className="needs-empty">{error}</p>}
          {!loading && !error && needs.length === 0 && <p className="needs-empty">No open needs match this search yet.</p>}
          {!loading && !error && needs.map((need) => (
            <article key={`${need.sourceType}-${need.id}`} className="need-card">
              <div className="need-card-top">
                <span>{need.isLocal ? 'Nearby' : need.sourceType}</span>
                <strong>{need.category}</strong>
              </div>
              <h3>{need.title}</h3>
              <p>{need.description}</p>
              <div className="need-meta">
                <span><Users className="h-4 w-4" /> {need.postedByName || 'Kingdom citizen'}</span>
                <span>{[need.city, need.state, need.country].filter(Boolean).join(', ') || 'Location not listed'}</span>
                <span>{need.responseCount || 0} response{Number(need.responseCount || 0) === 1 ? '' : 's'}</span>
              </div>
              <button type="button" onClick={() => setActiveNeed(need)}>I can help</button>
              <button type="button" onClick={() => showResponses(need)}>View responses</button>
            </article>
          ))}
        </section>
      </section>

      {viewingResponses && <div className="needs-modal-backdrop"><section className="needs-response-modal" role="dialog" aria-label="Need responses">
        <h2>Responses to {viewingResponses.title}</h2>
        {responseError && <p role="status">{responseError}</p>}
        {!responseError && !responses.length && <p>No responses yet.</p>}
        {responses.map((response) => <article key={response.id} className="need-card">
          <h3>{response.responderName || 'Member'}</h3>
          {response.offeredAssetName && <p>Gift offered: {response.offeredAssetName}</p>}
          <p>{response.message}</p><small>{response.status}</small>
        </article>)}
        <button type="button" onClick={() => setViewingResponses(null)}>Close</button>
      </section></div>}
      {activeNeed && (
        <div className="needs-modal-backdrop">
          <form className="needs-response-modal" onSubmit={submitResponse}>
            <span className="needs-kicker">Answer this need</span>
            <h2>{activeNeed.title}</h2>
            <p>{activeNeed.description}</p>
            <SelectField label="How can you help?" value={responseForm.responseKind} onChange={(value) => setResponseForm((current) => ({ ...current, responseKind: value }))}>
              {responseTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </SelectField>
            {selectInputForResponse()}
            {responseForm.responseKind === 'OfferItem' && <p>This is a gift, not a sale. Your item stays in your inventory until you arrange the handoff.</p>}
            <label className="needs-field">
              <span>Message</span>
              <textarea value={responseForm.message} onChange={(event) => setResponseForm((current) => ({ ...current, message: event.target.value }))} required rows={5} placeholder="Say plainly what you can offer, what questions you have, or who you think can help." />
            </label>
            <div className="needs-modal-actions">
              <button type="button" onClick={() => setActiveNeed(null)}>Cancel</button>
              <button type="submit" disabled={submitting}>{submitting ? 'Sending...' : 'Send response'}</button>
            </div>
            <div className="needs-response-shortcuts">
              <Link to={`/user-inventory/user/${encodeURIComponent(user?.sub || '')}?action=list-new`}><Package className="h-4 w-4" /> Add an item</Link>
              <Link to="/services"><Wrench className="h-4 w-4" /> Add a service</Link>
              <Link to="/marketplace"><Store className="h-4 w-4" /> Browse marketplace</Link>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

function SelectField({ label, value, onChange, children }) {
  return (
    <label className="needs-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {children}
      </select>
    </label>
  );
}
